import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Clock, Check, ArrowLeft, Plus, MessageCircle, X } from 'lucide-react';
import Reveal from '../components/Reveal';
import Loader, { ErrorState } from '../components/Loader';
import InquiryForm from '../components/InquiryForm';
import useAsync from '../hooks/useAsync';
import { getPackage, getAddOns } from '../lib/content';
import { formatINR } from '../lib/whatsapp';
import { useSettings } from '../context/SettingsContext';

export default function PackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showPricing } = useSettings();
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState([]);
  const [showInquiry, setShowInquiry] = useState(false);

  const { data: pkg, loading, error, reload } = useAsync(() => getPackage(id), [id]);
  const { data: addOnsData } = useAsync(getAddOns, []);
  const addOns = addOnsData || [];

  if (loading) return <Loader label="Loading package…" />;
  if (error) return <div className="section"><ErrorState message="Couldn't load this package." onRetry={reload} /></div>;

  if (!pkg) {
    return (
      <div className="section py-32 text-center">
        <p className="text-ink-300">Package not found.</p>
        <Link to="/packages" className="btn-primary mt-6">Back to packages</Link>
      </div>
    );
  }

  const toggle = (a) =>
    setSelected((s) =>
      s.find((x) => x.id === a.id) ? s.filter((x) => x.id !== a.id) : [...s, a]
    );

  const total = pkg.price + selected.reduce((s, a) => s + a.price, 0);

  const goCustomize = () => {
    const params = new URLSearchParams({
      pkg: pkg.id,
      addons: selected.map((a) => a.id).join(','),
    });
    navigate(`/customize?${params.toString()}`);
  };

  return (
    <div className="section py-10">
      <Link to="/packages" className="mb-6 inline-flex items-center gap-2 text-sm text-ink-300 hover:text-white">
        <ArrowLeft size={16} /> Back to packages
      </Link>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <Reveal>
          <motion.img
            key={active}
            initial={{ opacity: 0.4, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            src={pkg.gallery[active]}
            alt={pkg.title}
            className="h-80 w-full rounded-xl object-cover sm:h-[26rem]"
          />
          {pkg.gallery.length > 1 && (
            <div className="mt-3 flex gap-3">
              {pkg.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-20 w-24 overflow-hidden rounded-lg border-2 transition ${
                    active === i ? 'border-brand' : 'border-transparent opacity-60'
                  }`}
                >
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        {/* Info */}
        <Reveal delay={0.1} className="space-y-6">
          <div>
            <span className="rounded-pill bg-white/5 px-3 py-1 text-xs text-ink-300">{pkg.category}</span>
            <h1 className="mt-3 font-display text-3xl font-extrabold">{pkg.title}</h1>
            <div className="mt-3 flex items-center gap-4 text-sm text-ink-300">
              <span className="flex items-center gap-1"><Star size={15} className="fill-warning text-warning" /> {pkg.rating}</span>
              <span className="flex items-center gap-1"><Clock size={15} /> {pkg.duration}</span>
            </div>
          </div>

          <p className="text-ink-200">{pkg.summary}</p>

          <div>
            <h3 className="mb-3 font-display font-semibold">What's included</h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {pkg.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-ink-200">
                  <Check size={16} className="text-success" /> {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Add-ons */}
          <div>
            <h3 className="mb-3 font-display font-semibold">Enhance with add-ons</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {addOns.slice(0, 6).map((a) => {
                const on = selected.find((x) => x.id === a.id);
                return (
                  <button
                    key={a.id}
                    onClick={() => toggle(a)}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm transition ${
                      on ? 'border-brand bg-brand/10 text-white' : 'border-white/10 text-ink-200 hover:border-white/30'
                    }`}
                  >
                    <span>{a.title}</span>
                    <span className="flex items-center gap-2 text-ink-300">
                      {showPricing && formatINR(a.price)}
                      <span className={`grid h-5 w-5 place-items-center rounded-full ${on ? 'bg-brand text-white' : 'border border-white/20'}`}>
                        {on ? <Check size={12} /> : <Plus size={12} />}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pricing + CTA */}
          <div className="glass flex flex-wrap items-center justify-between gap-4 p-5">
            <div>
              <p className="text-xs text-ink-400">Estimated total</p>
              {showPricing ? (
                <p className="font-display text-2xl font-bold text-gradient">{formatINR(total)}</p>
              ) : (
                <p className="font-display text-lg font-semibold text-ink-200">Price on request</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowInquiry(true)} className="btn-whatsapp">
                Quick Inquiry <MessageCircle size={18} />
              </button>
              <button onClick={goCustomize} className="btn-primary">
                Customize & Inquire
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Quick Inquiry modal — saved with origin "package" */}
      <AnimatePresence>
        {showInquiry && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur"
            onClick={() => setShowInquiry(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="glass max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold">Inquire about {pkg.title}</h2>
                  <p className="text-xs text-ink-400">We’ll continue on WhatsApp instantly.</p>
                </div>
                <button onClick={() => setShowInquiry(false)}><X size={20} className="text-ink-400" /></button>
              </div>
              <InquiryForm pkg={pkg} addOns={selected} origin="package" compact />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
